import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Form, Input, InputNumber, Popconfirm, Popover, Select } from 'antd';
import { DownOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import CustomerPicker from '../components/Customer/CustomerPicker.jsx';
import { Band } from '../components/Order/orderFormShared.jsx';
import {
  FilterChip, LoadingRow, MenuList, PageBtn, PanelDrawer, StatTile,
  filterBtn, ghostBtn, primaryBtn, rowBtn, sheetCancelBtn, sheetOkBtn, miniLabel,
} from '../components/Stock/stockChrome.jsx';
import { PRIMARY, shellAntdTheme, shellVars, STATUS } from '../shellTokens';
import '../components/Order/orderForm.css';
import '../components/Stock/fabricForm.css';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const PAYMENT_METHODS = ['Cash', 'Bank', 'Couriar'];

const PRESETS = [
  { key: 'thisMonth', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'lastMonth', label: 'Last month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { key: 'last90', label: 'Last 90 days', range: () => [dayjs().subtract(90, 'day'), dayjs()] },
  { key: 'all', label: 'All time', range: () => null },
];

const fmtMoney = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/**
 * Fluid columns with floors, so the row fits whatever width the card is given
 * rather than forcing the table sideways. Same approach as the sales table.
 */
const GRID = [
  'minmax(64px,0.7fr)',  // date
  'minmax(0,2fr)',       // customer
  'minmax(0,1.8fr)',     // note / source
  'minmax(62px,0.8fr)',  // method
  'minmax(82px,1fr)',    // amount
  '54px',                // actions
].join(' ');

const cell = {
  padding: '11px 8px', font: '400 12.5px Outfit, sans-serif',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const numCell = { ...cell, textAlign: 'right', font: '600 12.5px monospace' };
const headCell = {
  padding: '0 8px 8px', ...miniLabel,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/** How many accounts the rail lists before the rest are left to the search box. */
const ACCOUNT_LIMIT = 14;

/**
 * Fabric payments — money in, and who is behind.
 *
 * A payment belongs to a customer's account rather than to an invoice: it can be
 * taken on a day the buyer bought nothing, and the backend allocates it to their
 * oldest unpaid consignment first. So this page reads as two things side by
 * side — what arrived over the chosen window, and where each account stands
 * across all time, which is the number worth chasing.
 */
function FabricPayments() {
  const axiosClient = useAxiosClient();
  const { darkMode, permissions } = useStateContext();
  const isMobile = useIsMobile();

  const canManage = (permissions || []).includes('MANAGE_FABRIC_STOCK');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [preset, setPreset] = useState('thisMonth');
  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);
  const [range, setRange] = useState(() => PRESETS[0].range());
  const [partyInput, setPartyInput] = useState('');
  const [party, setParty] = useState('');

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSeq, setSheetSeq] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [account, setAccount] = useState(null);

  const fetchPayments = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const size = overrides.pageSize ?? pageSize;
    const r = 'range' in overrides ? overrides.range : range;
    const partyTerm = 'party' in overrides ? overrides.party : party;

    setLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/payments', {
        params: {
          page: p,
          perPage: size,
          from: r?.[0] ? r[0].format('YYYY-MM-DD') : undefined,
          to: r?.[1] ? r[1].format('YYYY-MM-DD') : undefined,
          party: partyTerm || undefined,
        },
      });
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
      setTotal(res.data.meta?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load payments');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, pageSize, range, party]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // The picked buyer's account, so the form says what they owe before the
  // amount is entered rather than after it is saved.
  const customerId = formValues.customer?.id ?? null;
  useEffect(() => {
    if (!customerId || !sheetOpen) {
      setAccount(null);
      return undefined;
    }

    let live = true;
    axiosClient.get('/fabricStock/payments/balance', { params: { customerId } })
      .then((res) => { if (live) setAccount(res.data.balance || null); })
      .catch(() => { if (live) setAccount(null); });

    return () => { live = false; };
  }, [axiosClient, customerId, sheetOpen]);

  const applyPreset = (key) => {
    if (key === 'custom') {
      setPreset('custom');
      return;
    }
    const found = PRESETS.find((p) => p.key === key);
    const next = found ? found.range() : null;
    setPreset(key);
    setRange(next);
    setPage(1);
    fetchPayments({ range: next, page: 1 });
  };

  const applyRange = (value) => {
    setPreset('custom');
    setRange(value);
    setPage(1);
    fetchPayments({ range: value, page: 1 });
  };

  const applyParty = (value) => {
    setParty(value);
    setPage(1);
    fetchPayments({ party: value, page: 1 });
  };

  const openSheet = () => {
    setSheetSeq((n) => n + 1);
    setFormValues({ date: dayjs(), paymentMethod: 'Cash' });
    setAccount(null);
    setSheetOpen(true);
  };

  const submit = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);
    try {
      await axiosClient.post('/fabricStock/payments', {
        customerId: v.customer.id,
        receivedOn: v.date.format('YYYY-MM-DD'),
        amount: v.amount,
        paymentMethod: v.paymentMethod || null,
        note: v.note || null,
      });
      toast.success('Payment recorded.');
      setSheetOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not record the payment');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axiosClient.delete(`/fabricStock/payments/${id}`);
      toast.success('Payment removed.');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not remove the payment');
    }
  };

  /** Exports the page on screen — the accounts rail already covers all time. */
  const handleExport = () => {
    if (!rows.length) {
      toast.info('Nothing to export');
      return;
    }
    const head = ['Date', 'Customer', 'Phone', 'Method', 'Note', 'Amount'];
    const body = rows.map((r) => [
      r.receivedOn,
      r.customer?.name,
      r.customer?.phone,
      r.paymentMethod,
      r.note,
      r.amount,
    ]);
    const csv = [head, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `fabric-payments-${dayjs().format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rangeLabel = useMemo(() => {
    if (preset !== 'custom') return PRESETS.find((p) => p.key === preset)?.label || 'All time';
    if (!range?.[0]) return 'All time';
    return `${range[0].format('D MMM')} – ${range[1].format('D MMM YYYY')}`;
  }, [preset, range]);

  const accounts = summary?.accounts || [];
  const owing = accounts.filter((a) => a.due > 0.5);
  const shownAccounts = owing.length ? owing.slice(0, ACCOUNT_LIMIT) : accounts.slice(0, ACCOUNT_LIMIT);
  const hiddenAccounts = (owing.length ? owing.length : accounts.length) - shownAccounts.length;

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const rootStyle = shellVars(darkMode);

  return (
    <div className="et-pay" style={rootStyle}>
      <style>{`
        .et-pay{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-pay__row:hover{background:var(--hover)}
        .et-pay__ghost:hover{background:var(--hover)}
        .et-pay__acct:hover{background:var(--hover)}
        .et-pay__body{display:flex;flex-direction:column;gap:14px;min-height:0}
        @media (min-width:1100px){
          .et-pay__body{display:grid;grid-template-columns:minmax(0,1fr) 320px;align-items:start;overflow:hidden}
          .et-pay__rail{overflow-y:auto;max-height:100%;display:flex;flex-direction:column;gap:14px}
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Fabric Payments</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Money received against customer accounts · {rangeLabel}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="et-pay__ghost" onClick={handleExport} style={ghostBtn}>
            <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
          </button>
          {canManage && (
            <button type="button" onClick={openSheet} style={primaryBtn}>
              Record payment
            </button>
          )}
        </div>
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
            <StatTile
              icon="৳" tint={STATUS.ok} label="Received"
              value={fmtMoney(summary.received)}
              foot={`${summary.payments} payment${summary.payments === 1 ? '' : 's'} · ${rangeLabel.toLowerCase()}`}
            />
            <StatTile
              icon="!" tint={summary.outstanding > 0 ? STATUS.warn : 'var(--muted)'} label="Outstanding"
              value={fmtMoney(summary.outstanding)}
              foot={owing.length ? `${owing.length} account${owing.length === 1 ? '' : 's'} behind` : 'all settled'}
            />
            <StatTile
              icon="↩" tint={PRIMARY} label="Advance held"
              value={fmtMoney(summary.advance)}
              foot="credit against future fabric"
            />
            <StatTile
              icon="◷" tint="var(--muted)" label="Accounts"
              value={String(accounts.length)}
              foot="customers who have bought or paid"
            />
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Popover
            open={rangeMenuOpen}
            onOpenChange={setRangeMenuOpen}
            trigger="click"
            placement="bottomLeft"
            content={
              <MenuList
                darkMode={darkMode}
                active={preset}
                items={[...PRESETS.map((p) => ({ key: p.key, label: p.label })), { key: 'custom', label: 'Custom range' }]}
                onSelect={(key) => { applyPreset(key); setRangeMenuOpen(false); }}
              />
            }
          >
            <button type="button" style={filterBtn}>
              {rangeLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
            </button>
          </Popover>

          {preset === 'custom' && (
            <RangePicker
              value={range}
              onChange={applyRange}
              format="D MMM YYYY"
              style={{ height: 38 }}
            />
          )}

          <Input
            allowClear
            value={partyInput}
            onChange={(e) => setPartyInput(e.target.value)}
            onPressEnter={() => applyParty(partyInput.trim())}
            onBlur={() => { if (partyInput.trim() !== party) applyParty(partyInput.trim()); }}
            prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
            placeholder="Customer name or phone"
            style={{ width: 260, height: 38 }}
          />

          {party && <FilterChip label="Customer" value={party} onClear={() => { setPartyInput(''); applyParty(''); }} />}
        </div>

        <div className="et-pay__body">
          {/* Payments */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 12px 8px', minWidth: 0 }}>
            {!isMobile && (
              <div style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)' }}>
                <div style={{ ...headCell, paddingLeft: 12 }}>Date</div>
                <div style={headCell}>Customer</div>
                <div style={headCell}>Note</div>
                <div style={headCell}>Method</div>
                <div style={{ ...headCell, textAlign: 'right' }}>Amount</div>
                <div style={headCell} />
              </div>
            )}

            {loading && <LoadingRow />}

            {!loading && rows.length === 0 && <EmptyState />}

            {!loading && !isMobile && rows.map((r) => (
              <div
                key={r.id}
                className="et-pay__row"
                style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)', alignItems: 'center' }}
              >
                <div style={{ ...cell, paddingLeft: 12, color: 'var(--muted)', fontFamily: 'monospace', fontSize: 12 }}>
                  {dayjs(r.receivedOn).format('D MMM')}
                </div>
                <div style={{ ...cell, fontWeight: 600 }}>
                  {r.customer?.name || 'Unknown'}
                  {r.customer?.phone && (
                    <span style={{ color: 'var(--dim)', fontWeight: 400 }}> · {r.customer.phone}</span>
                  )}
                </div>
                <div style={{ ...cell, color: 'var(--muted)' }}>
                  {/* A payment taken with a sale is labelled as such, so a row
                      with no note is not mistaken for a missing one. */}
                  {r.note || (r.fabricOutflowId ? 'With a sale' : '—')}
                </div>
                <div style={{ ...cell, color: 'var(--muted)' }}>{r.paymentMethod || '—'}</div>
                <div style={{ ...numCell, color: STATUS.ok }}>{fmtMoney(r.amount)}</div>
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {canManage && (
                    <Popconfirm
                      title="Remove this payment?"
                      description="Whatever it was settling reopens."
                      okText="Remove"
                      cancelText="Keep"
                      onConfirm={() => remove(r.id)}
                    >
                      <button type="button" style={rowBtn}>×</button>
                    </Popconfirm>
                  )}
                </div>
              </div>
            ))}

            {!loading && isMobile && rows.map((r) => (
              <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ font: '600 13px Outfit, sans-serif' }}>{r.customer?.name || 'Unknown'}</span>
                  <span style={{ font: '700 13px monospace', color: STATUS.ok }}>{fmtMoney(r.amount)}</span>
                </div>
                <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 4 }}>
                  {dayjs(r.receivedOn).format('D MMM YYYY')}
                  {r.paymentMethod && <> · {r.paymentMethod}</>}
                  {(r.note || r.fabricOutflowId) && <> · {r.note || 'With a sale'}</>}
                </div>
                {canManage && (
                  <Popconfirm title="Remove this payment?" okText="Remove" cancelText="Keep" onConfirm={() => remove(r.id)}>
                    <button type="button" style={{ ...rowBtn, marginTop: 8 }}>Remove</button>
                  </Popconfirm>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 4px', flexWrap: 'wrap' }}>
              <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                {total} payment{total === 1 ? '' : 's'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                  value={pageSize}
                  onChange={(size) => { setPageSize(size); setPage(1); fetchPayments({ pageSize: size, page: 1 }); }}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
                  style={{ width: 110 }}
                />
                <PageBtn disabled={page <= 1} onClick={() => { setPage(page - 1); fetchPayments({ page: page - 1 }); }}>‹</PageBtn>
                <span style={{ font: '600 12px monospace', color: 'var(--muted)' }}>{page} / {lastPage}</span>
                <PageBtn disabled={page >= lastPage} onClick={() => { setPage(page + 1); fetchPayments({ page: page + 1 }); }}>›</PageBtn>
              </div>
            </div>
          </div>

          {/* Accounts — all time, deliberately. Who owes what does not depend on
              which window the payments list is pointed at. */}
          {accounts.length > 0 && (
            <div className="et-pay__rail">
              <Panel
                title="Accounts"
                hint={owing.length ? 'Owing first · all time' : 'All settled'}
              >
                {shownAccounts.map((a) => (
                  <div
                    key={a.customerId}
                    role="button" tabIndex={0}
                    className="et-pay__acct"
                    title={`Filter to ${a.party}`}
                    onClick={() => { setPartyInput(a.party); applyParty(a.party); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setPartyInput(a.party); applyParty(a.party); } }}
                    style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, padding: 8, borderRadius: 8, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: '600 12.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.party}
                      </div>
                      <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>
                        {fmtMoney(a.paid)} of {fmtMoney(a.billed)} paid
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ font: '700 12.5px monospace', color: a.due > 0.5 ? STATUS.warn : STATUS.ok }}>
                        {a.due > 0.5 ? fmtMoney(a.due) : '—'}
                      </div>
                      {a.advance > 0.5 && (
                        <div style={{ font: '500 10.5px Outfit, sans-serif', color: PRIMARY }}>
                          {fmtMoney(a.advance)} advance
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {hiddenAccounts > 0 && (
                  <div style={{ padding: '8px 8px 0', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                    {hiddenAccounts} more — search by name or phone above.
                  </div>
                )}
              </Panel>
            </div>
          )}
        </div>

        {/* Record payment */}
        <PanelDrawer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 560}
          title="Record payment"
          subtitle="Money in against a customer’s account"
          bodyPadding={0}
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setSheetOpen(false)} style={sheetCancelBtn}>Cancel</button>
              <button type="button" onClick={submit} disabled={saving} style={{ ...sheetOkBtn, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Record payment'}
              </button>
            </div>
          }
        >
          <div className="et-orderform">
            <Form
              // Remounted per opening so a cancelled form leaves nothing behind.
              key={sheetSeq}
              form={form}
              layout="vertical"
              // "div", not `false`. Vertical layout is nothing but the CSS that
              // antd's `ant-form-vertical` class selects, and `component={false}`
              // renders no element to put it on — so every label falls back to
              // sitting beside its control and the row breaks. Same reasoning as
              // the sheet on the Fabric Store.
              component="div"
              initialValues={{ date: dayjs(), paymentMethod: 'Cash' }}
              onValuesChange={(_changed, all) => setFormValues(all)}
            >
              <Band label="Customer">
                <Form.Item
                  name="customer"
                  rules={[{ required: true, message: 'Find the customer by phone, or add them.' }]}
                >
                  <CustomerPicker darkMode={darkMode} />
                </Form.Item>
                {account && (
                  <div className="et-of-band" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
                    <Fact label="Billed to date" value={fmtMoney(account.billed)} />
                    <Fact label="Received" value={fmtMoney(account.paid)} tint={STATUS.ok} />
                    <Fact
                      label={account.advance > 0.5 ? 'Advance held' : 'Outstanding'}
                      value={fmtMoney(account.advance > 0.5 ? account.advance : account.outstanding)}
                      tint={account.advance > 0.5 ? PRIMARY : account.outstanding > 0.5 ? STATUS.warn : 'var(--muted)'}
                    />
                  </div>
                )}
              </Band>

              <Band label="Payment">
                <div className="et-of-cols3">
                  <Form.Item name="date" label="Received on" rules={[{ required: true, message: 'Please pick a date!' }]}>
                    <DatePicker allowClear={false} style={{ width: '100%' }} format="D MMM YYYY" />
                  </Form.Item>
                  <Form.Item
                    name="amount"
                    label="Amount"
                    rules={[
                      { required: true, message: 'Enter an amount!' },
                      { type: 'number', min: 0.01, message: 'Must be more than zero!' },
                    ]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                  <Form.Item name="paymentMethod" label="Method">
                    <Select options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))} />
                  </Form.Item>
                </div>
                <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>
                  Settles their oldest unpaid consignment first — it is not tied to one invoice.
                </div>
              </Band>

              <Band label="Note" last>
                <Form.Item name="note">
                  <Input placeholder="Optional — cheque number, who handed it over" maxLength={255} />
                </Form.Item>
              </Band>
            </Form>
          </div>
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    <div style={{ flex: 'none', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ font: '700 13px Outfit, sans-serif' }}>{title}</span>
        {hint && <span style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** One figure from the buyer's running account, on the payment form. */
function Fact({ label, value, tint }) {
  return (
    <div>
      <div style={{ font: '600 10px Outfit, sans-serif', letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--dim)' }}>{label}</div>
      <div style={{ font: '700 13px monospace', color: tint || 'var(--text)' }}>{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>৳</span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>No payments in this range</div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        Money taken with a sale shows up here too — it is recorded against the customer, not the invoice.
      </div>
    </div>
  );
}

export default FabricPayments;
